import * as React from 'react';
import { Field, useFormikContext } from 'formik';

import DocsHelper from 'util/DocsHelper';
import { Col, Row } from 'common/components/layout-styles';
import { FormikInput, Icon } from 'components/common';
import { Button, ControlLabel, Alert } from 'components/bootstrap';
import { FormikMarkdownEditor } from 'security-app/components/common/FormikComponents';
import type {
  EventProceduresAPIType,
  EventProcedureStepAPIType,
} from 'security-app/hooks/api/eventProceduresAPI.types';

import StepListFormik from './StepListFormik';

type Props = {
  onNewStep: () => void;
  onFromLibrary: () => void;
  onViewStep: (stepId: string) => void;
  onRemoveStep?: (stepId: string) => void;
};

function EventProcedureFormFields({ onNewStep, onFromLibrary, onViewStep, onRemoveStep = () => {} }: Props) {
  const { values, setFieldValue, setFieldTouched } =
    useFormikContext<EventProceduresAPIType<Array<EventProcedureStepAPIType>>>();

  const handleRemoveStep = React.useCallback(
    (stepId: string) => {
      const { steps } = values;
      const newSteps = steps.filter((step: EventProcedureStepAPIType) => step.id !== stepId);
      setFieldValue('steps', newSteps);
      setFieldTouched('steps', true);
      onRemoveStep?.(stepId);
    },
    [setFieldTouched, setFieldValue, onRemoveStep, values],
  );

  return (
    <Col $gap="xl">
      <Col $align="stretch">
        <FormikInput type="text" name="title" id="title" label="Title" required />
        <Field
          id="event_procedure_description"
          name="description"
          component={FormikMarkdownEditor}
          label="Description"
          height={150}
        />
      </Col>
      <Col $gap="md">
        <Row $align="center" $justify="space-between">
          <Row $align="center" $gap="sm">
            <ControlLabel>Steps</ControlLabel>
            <Button bsSize="sm" onClick={onNewStep}>
              <Icon name="add" size="sm" />
              New Step
            </Button>
            <Button bsSize="sm" onClick={onFromLibrary}>
              <Icon name="assignment_add" size="sm" />
              From Library
            </Button>
          </Row>
          <Button
            bsSize="sm"
            bsStyle="link"
            href={DocsHelper.toString(DocsHelper.PAGES.EVENT_PROCEDURES)}
            target="_blank"
            rel="noopener noreferrer">
            Event Procedures Documentation
          </Button>
        </Row>
      </Col>
      {values?.steps?.length < 1 ? (
        <Alert style={{ margin: 0, width: '100%' }}>Add steps to the procedure from the options above</Alert>
      ) : (
        <Row>
          <Field name="steps" component={StepListFormik} onViewStep={onViewStep} onRemoveStep={handleRemoveStep} />
        </Row>
      )}
    </Col>
  );
}

export default EventProcedureFormFields;
